import { Checkbox } from '@mui/material'
import {
  allAvailableEventTypes,
  EVENT_SUBTYPE_COUPLE_THERAPY,
  EVENT_SUBTYPE_INDIVIDUAL_THERAPY,
  EVENT_SUBTYPE_THERAPIST_ASSESSMENT,
  platformOptions,
  VISIT_EVENT_TYPES,
  VISIT_EVENT_TYPES_ARRAY,
} from 'src/consumer/constants'
import {
  buildScreeningTargetSpecialtyOptions,
  normalizeScreeningSpecialtyBucket,
} from 'src/consumer/helpers/screeningSpecialtyUtils'
import { SuggestedAssessmentsSelectMenu } from 'src/mentor/components/PrescriptionMakingPage/PrescriptionMakingPageComponents'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { GenericDisplayFlex } from 'src/mentor/layout/Styles'
import {
  GenericModalHeadlineLevelTwo,
  StyledInputSelect,
} from 'src/mentor/components/MentorAvailabilityPage/MentorAvailabilityPageStyles'

export default function EventTypeConfiguration({
  basicEventObject,
  setBasicEventObject,
  displayNamesOfEventTypes,
  mentorProfileData,
  forceShowDoctorEventTypes,
  forceShowTherapistEventTypes,
  changeLocationBasedOnMentorOrClinicLocation,
  isDisabled,
}) {
  return (
    <>
      {basicEventObject?.type !== allAvailableEventTypes?.webinar && (
        <div>
          <GenericModalHeadlineLevelTwo>Type</GenericModalHeadlineLevelTwo>
          <StyledInputSelect
            value={basicEventObject?.type}
            onChange={(e) => {
              const nextType = e.target.value
              setBasicEventObject((prev) => ({
                ...prev,
                type: nextType,
                visitEventType: VISIT_EVENT_TYPES.regular,
                ...(nextType === allAvailableEventTypes.therapySession
                  ? { subtype: prev.subtype || EVENT_SUBTYPE_INDIVIDUAL_THERAPY }
                  : {}),
                ...(nextType === allAvailableEventTypes.screening
                  ? {
                      screeningAllowedIfPossible:
                        prev.screeningAllowedIfPossible !== undefined
                          ? prev.screeningAllowedIfPossible
                          : true,
                    }
                  : {}),
              }))
            }}
            disabled={isDisabled}
          >
            <option value={allAvailableEventTypes.oneOnOne}>
              {displayNamesOfEventTypes?.[allAvailableEventTypes.oneOnOne]}
            </option>
            <option value={allAvailableEventTypes.screening}>
              {displayNamesOfEventTypes?.[allAvailableEventTypes.screening]}
            </option>
            <option value={allAvailableEventTypes.interview}>
              {displayNamesOfEventTypes?.[allAvailableEventTypes.interview]}
            </option>
            {((mentorProfileData?.allowedToPrescribe && getAllRolesInBoolean().isDoctor) ||
              forceShowDoctorEventTypes) && (
              <option value={allAvailableEventTypes.consultation}>
                {displayNamesOfEventTypes?.[allAvailableEventTypes.consultation]}
              </option>
            )}
            {(mentorProfileData?.services?.live || forceShowTherapistEventTypes) && (
              <>
                <option value={allAvailableEventTypes.therapySession}>
                  {displayNamesOfEventTypes?.[allAvailableEventTypes.therapySession]}
                </option>
                <option value={allAvailableEventTypes.assessment}>
                  {displayNamesOfEventTypes?.[allAvailableEventTypes.assessment]}
                </option>
              </>
            )}
          </StyledInputSelect>
        </div>
      )}

      {basicEventObject?.type === allAvailableEventTypes.screening && (
        <div>
          Note: Screening events should be offline and associated to a clinic. Patients in clinic
          can opt to go through this step before consulting a psychiatrist.
        </div>
      )}

      {basicEventObject?.type === allAvailableEventTypes.screening && (
        <>
          <div style={{ marginTop: 12 }}>
            <GenericModalHeadlineLevelTwo>Screening target specialty</GenericModalHeadlineLevelTwo>
            <StyledInputSelect
              value={normalizeScreeningSpecialtyBucket(
                basicEventObject?.targetSpecialtySlug || basicEventObject?.screeningType || '',
              )}
              onChange={(e) =>
                setBasicEventObject((prev) => ({
                  ...prev,
                  targetSpecialtySlug: e.target.value,
                }))
              }
              disabled={isDisabled}
            >
              {buildScreeningTargetSpecialtyOptions(
                mentorProfileData?.targetSpecialties || [],
              ).map((opt) => (
                <option key={opt.slug === '' ? 'default' : opt.slug} value={opt.slug}>
                  {opt.title}
                </option>
              ))}
            </StyledInputSelect>
          </div>
          <GenericDisplayFlex style={{ marginTop: 12 }}>
            <div>Allow screening when scheduling permits</div>
            <Checkbox
              size="small"
              disabled={isDisabled}
              sx={{
                padding: 0,
                marginLeft: 1,
                '&.Mui-checked': { color: 'blue' },
              }}
              checked={basicEventObject?.screeningAllowedIfPossible !== false}
              onChange={(event) => {
                const { checked } = event.target
                setBasicEventObject((prev) => ({
                  ...prev,
                  screeningAllowedIfPossible: checked,
                }))
              }}
            />
          </GenericDisplayFlex>
        </>
      )}

      {basicEventObject?.type === allAvailableEventTypes.therapySession && (
        <div style={{ marginTop: 12 }}>
          <GenericModalHeadlineLevelTwo>Therapy format</GenericModalHeadlineLevelTwo>
          <StyledInputSelect
            value={basicEventObject?.subtype || EVENT_SUBTYPE_INDIVIDUAL_THERAPY}
            onChange={(e) =>
              setBasicEventObject((prev) => ({ ...prev, subtype: e.target.value }))
            }
            disabled={isDisabled}
          >
            <option value={EVENT_SUBTYPE_INDIVIDUAL_THERAPY}>Individual therapy</option>
            <option value={EVENT_SUBTYPE_COUPLE_THERAPY}>Couple therapy</option>
            <option value={EVENT_SUBTYPE_THERAPIST_ASSESSMENT}>Therapist assessment</option>
          </StyledInputSelect>
        </div>
      )}

      {basicEventObject?.type === allAvailableEventTypes.consultation && (
        <div>
          <GenericModalHeadlineLevelTwo>Event Visit Type</GenericModalHeadlineLevelTwo>
          <StyledInputSelect
            value={basicEventObject?.visitEventType || ''}
            onChange={(e) => {
              setBasicEventObject((prev) => ({ ...prev, visitEventType: e.target.value }))
            }}
            disabled={isDisabled}
          >
            <option value="" hidden>
              None
            </option>
            {VISIT_EVENT_TYPES_ARRAY?.map((item) => {
              return (
                <option key={item?.id} value={item?.id}>
                  {item?.title}
                </option>
              )
            })}
          </StyledInputSelect>
        </div>
      )}

      {basicEventObject?.type === allAvailableEventTypes.assessment && (
        <SuggestedAssessmentsSelectMenu
          isNotCreatable
          selectedAssessments={basicEventObject?.psychAssessmentIds}
          setSelectedAssessments={(value) => {
            setBasicEventObject((prev) => ({ ...prev, psychAssessmentIds: value }))
          }}
          searchApiSignature={`/api/secure/teacher/psych-assessment/search`}
          customPlaceholder="Search assessments to attach to this event..."
          doctorSpecializations={mentorProfileData?.specializedCategories}
          disabled={isDisabled}
        />
      )}

      <div>
        <GenericModalHeadlineLevelTwo>Meeting Source(Platform)</GenericModalHeadlineLevelTwo>
        <StyledInputSelect
          value={basicEventObject?.meetingSrc}
          onChange={(e) => {
            setBasicEventObject((prev) => ({ ...prev, meetingSrc: e.target.value }))
            changeLocationBasedOnMentorOrClinicLocation(
              basicEventObject?.location,
              basicEventObject?.contextId,
            )
          }}
          disabled={isDisabled}
        >
          {platformOptions?.map((item) => {
            return (
              <option key={item?.id} value={item?.id}>
                {item?.name}
              </option>
            )
          })}
        </StyledInputSelect>
      </div>
    </>
  )
}
